import React, { useContext, useMemo, useState } from "react";
import {
  Layout,
  Card,
  TimePicker,
  Switch,
  Button,
  message,
  Typography,
  Space,
  Row,
  Col,
  Alert,
} from "antd";
import { Clock, Calendar as CalendarIcon } from "lucide-react";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";
import { CompanyContext } from "../../contexts/company-context";
import { AuthenticationContext } from "../../contexts/authentication-context";
import { DayPreference } from "../../types/preferences";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const DAYS = [
  { name: "Sunday", index: 0 },
  { name: "Monday", index: 1 },
  { name: "Tuesday", index: 2 },
  { name: "Wednesday", index: 3 },
  { name: "Thursday", index: 4 },
  { name: "Friday", index: 5 },
  { name: "Saturday", index: 6 },
];

const Preferences: React.FC = () => {
  const employeeOps = useMemo(() => new EmployeeOperation(), []);
  const { selectedCompany } = useContext(CompanyContext);
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const [isSaving, SetIsSaving] = useState<boolean>(false);
  const [preferences, setPreferences] = useState<DayPreference[]>(
    DAYS.map((day) => ({
      dayIndex: day.index,
      available: false,
      startTime: null,
      endTime: null,
    }))
  );

  useMemo(() => {
    employeeOps
      .getEmployeeAvailabilityAsync(
        selectedCompany?.corporationId as number,
        userInfo?.employeeCorporation
          ?.filter((x) => x.corporationId === selectedCompany?.corporationId)
          .map((x) => x.employeeId)
          .at(0) as string
      )
      .then((x) => {
        if (
          x.statusCode === 200 &&
          Array.isArray(x.data) &&
          x.data.length > 0
        ) {
          //Mapping Over Previous State: Instead of completely replacing the state, we're using prev.map to iterate over the existing employeeActivity.
          //Finding New Data: For each item in the previous state, we look for corresponding new data using find.
          //Updating Availability: If new data is found, we merge it with the existing item using the spread operator. If not, we return the existing item unchanged.
          setPreferences((prev) =>
            prev.map((item) => {
              const newData = x.data?.find((y) => y.dayIndex === item.dayIndex);
              return newData ? { ...item, available: newData.available } : item;
            })
          );
        }
      });
  }, [employeeOps, selectedCompany]);

  const updatePreference = (
    dayIndex: number,
    updates: Partial<DayPreference>
  ) => {
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.dayIndex === dayIndex ? { ...pref, ...updates } : pref
      )
    );
  };

  const handleSave = () => {
    // Make the API call here and only update the local model
    // if it was successful.
    SetIsSaving(true);
    const updatedPref = preferences.map((item) => ({
      operationType: 2,
      op: "Replace",
      value: {
        dayIndex: item.dayIndex,
        available: item.available,
      },
    }));

    employeeOps
      .patchEmployeeAvailabilityAsync(
        selectedCompany?.corporationId as number,
        updatedPref
      )
      .then((x) => {
        if (x.statusCode == 200) {
          message.success("Schedule preferences saved successfully!");
          SetIsSaving(false);
        } else {
          message.error("There was an error trying to process your request!");
          SetIsSaving(false);
        }
      });

    console.log("Saving preferences:", preferences);
  };

  return (
    <div style={{ maxWidth: 800, margin: "44px" }}>
      <div style={{ marginBottom: 24 }}>
        <Space align="start" size={16}>
          <CalendarIcon style={{ width: 32, height: 32, color: "#1890ff" }} />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Schedule Preferences
            </Title>
            <Text type="secondary">
              Set your preferred working hours for each day
            </Text>
          </div>
        </Space>
      </div>

      <Alert
        message="Note: Selecting preferences does not guarantee that they will be honored."
        type="info"
        showIcon
      />
      <br />
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {DAYS.map((day) => {
          const preference = preferences.find((p) => p.dayIndex === day.index)!;

          return (
            <Card key={day.index}>
              <Row align="middle" gutter={24}>
                <Col span={8}>
                  <Space size={16}>
                    <Switch
                      checked={preference.available}
                      onChange={(checked) =>
                        updatePreference(day.index, { available: checked })
                      }
                    />
                    <Text strong>{day.name}</Text>
                  </Space>
                </Col>
                <Col span={16}>
                  <Row gutter={16} align="middle">
                    <Col>
                      <Space>
                        <Clock
                          style={{ width: 16, height: 16, color: "#8c8c8c" }}
                        />
                        <TimePicker
                          style={{ width: 120 }}
                          format="h:mm A"
                          placeholder="Start time"
                          disabled={!preference.available}
                          value={
                            preference.startTime
                              ? dayjs(preference.startTime, "HH:mm")
                              : null
                          }
                          onChange={(time) =>
                            updatePreference(day.index, {
                              startTime: time ? time.format("HH:mm") : null,
                            })
                          }
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Text type="secondary">to</Text>
                    </Col>
                    <Col>
                      <TimePicker
                        style={{ width: 120 }}
                        format="h:mm A"
                        placeholder="End time"
                        disabled={!preference.available}
                        value={
                          preference.endTime
                            ? dayjs(preference.endTime, "HH:mm")
                            : null
                        }
                        onChange={(time) =>
                          updatePreference(day.index, {
                            endTime: time ? time.format("HH:mm") : null,
                          })
                        }
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          );
        })}
      </Space>

      <div style={{ marginTop: 24, textAlign: "right" }}>
        <Button type="primary" onClick={handleSave} loading={isSaving}>
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default Preferences;
